import { isCheckpointStatusCompleted } from '@/features/player/components/player-profile/player-progress-tracker/progress-tracker.utils';
import type { PlayerProgressBadge, PlayerProgressItem } from '@/features/player/components/types';

export type LevelStatusKey = 'completed' | 'incomplete' | 'partial';

/**
 * Level row: derive from badge checkpoint statuses.
 *
 * Rationale: `badgeEarned` may remain false until all checkpoints complete,
 * but UI needs "partial" as soon as any checkpoint is completed.
 */
export function levelAggregateStatusFromBadges(
  item: PlayerProgressItem | undefined,
): LevelStatusKey {
  const badges = item?.badges ?? [];
  if (badges.length === 0) return 'incomplete';
  const badgeAgg = badges.map(badgeAggregateStatusFromCheckpoints);
  if (badgeAgg.every(s => s === 'completed')) return 'completed';
  if (badgeAgg.every(s => s === 'incomplete')) return 'incomplete';
  return 'partial';
}

/**
 * Skill (badge) row: same rule over checkpoints (completed vs not completed).
 */
export function badgeAggregateStatusFromCheckpoints(badge: PlayerProgressBadge): LevelStatusKey {
  const cps = badge.checkpoints ?? [];
  if (cps.length === 0) return 'incomplete';
  const done = cps.map(cp => isCheckpointStatusCompleted(cp?.status));
  if (done.every(Boolean)) return 'completed';
  if (!done.some(Boolean)) return 'incomplete';
  return 'partial';
}

/** Checkpoint row: binary for display. */
export function checkpointDisplayStatus(
  status: string | undefined | null,
): 'completed' | 'incomplete' {
  return isCheckpointStatusCompleted(status) ? 'completed' : 'incomplete';
}
